import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/api';
import { SocialAuthButton } from './SocialAuthButton';
import type { SocialAuthProvider } from './SocialAuthButton';
import { clearSocialAuthContext, storeSocialAuthContext } from '@/utils/socialAuthContext';
import { resetSocialCallback } from '@/utils/socialAuthCallback';
import { useToast } from '@/primer/components';

type SocialLoginButtonsProps = {
  onError?: (error: string) => void;
};

export function SocialLoginButtons({ onError }: SocialLoginButtonsProps) {
  const { socialLogin } = useAuth();
  const { showToast } = useToast();
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const pending = useRef(false);

  useEffect(() => {
    const recover = (event: PageTransitionEvent) => {
      if (!event.persisted || !pending.current) return;
      pending.current = false;
      setLoadingProvider(null);
      const message = 'Sign-in was cancelled. Please try again.';
      if (onError) onError(message);
      else showToast({ variant: 'error', message });
    };
    window.addEventListener('pageshow', recover);
    return () => window.removeEventListener('pageshow', recover);
  }, [onError, showToast]);

  const handleSocialLogin = async (provider: SocialAuthProvider) => {
    if (pending.current) return;
    pending.current = true;
    const providerName = provider;
    setLoadingProvider(providerName);
    try {
      storeSocialAuthContext({ kind: 'direct', provider });
      await socialLogin(provider);
    } catch {
      resetSocialCallback();
      clearSocialAuthContext();
      setLoadingProvider(null);
      pending.current = false;
      const message = `Unable to sign in with ${providerName}. Please try again.`;
      if (onError) onError(message);
      else showToast({ variant: 'error', message });
    }
  };

  const isLoading = loadingProvider !== null;
  return (
    <div className="flex flex-col gap-4">
      <SocialAuthButton
        provider="Facebook"
        onClick={() => void handleSocialLogin('Facebook')}
        disabled={isLoading}
        isLoading={loadingProvider === 'Facebook'}
        data-testid="facebook-social-button"
      />

      <SocialAuthButton
        provider="Google"
        onClick={() => void handleSocialLogin('Google')}
        disabled={isLoading}
        isLoading={loadingProvider === 'Google'}
        data-testid="google-social-button"
      />

      <SocialAuthButton
        provider="Apple"
        onClick={() => void handleSocialLogin('Apple')}
        disabled={isLoading}
        isLoading={loadingProvider === 'Apple'}
        data-testid="apple-social-button"
      />
    </div>
  );
}

export default SocialLoginButtons;
